import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import logo from './assets/prism-logo.svg';
import { LandingPage } from './pages/LandingPage';
import { StorefrontPage } from './pages/StorefrontPage';
import { DashboardPage } from './pages/DashboardPage';
import { MarketLandingPage } from './pages/MarketLandingPage';
import { NeighborhoodLandingPage } from './pages/NeighborhoodLandingPage';
import { BuildingLandingPage } from './pages/BuildingLandingPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { CheckoutCancelPage } from './pages/CheckoutCancelPage';
import { AdminPage } from './pages/AdminPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UserMenu } from './components/UserMenu';
import { AnalyticsPackagePanel } from './components/AnalyticsPackagePanel';
import { MyLensesPanel } from './components/MyLensesPanel';
import { LensSyncManager } from './components/LensSyncManager';
import { useAnalyticsPackage } from './context/AnalyticsPackageContext';
import { useUserContext } from './context/UserContext';
import { useApi } from './hooks/useApi';

function Nav() {
  const { isAuthenticated, isLoading } = useAuth0();
  const { authFetch } = useApi();
  const { items, pulseKey, setPanelOpen, setMyLensesPanelOpen } = useAnalyticsPackage();
  const { me, showPdfLens } = useUserContext();
  const location = useLocation();

  // On a chart view (/dashboard?marketId=X&chart=Y), DashboardPage's URL-parsing
  // effect only runs on mount, so linking to the same /dashboard route wouldn't
  // trigger its redirect back to the market landing page. Link straight to the
  // market instead so this is always a real route change.
  const dashboardMarketId = location.pathname === '/dashboard'
    ? new URLSearchParams(location.search).get('marketId')
    : null;
  const logoHref = dashboardMarketId ? `/market/${dashboardMarketId}` : '/dashboard';

  const handleBilling = async () => {
    try {
      const res = await authFetch('/api/checkout/portal', { method: 'POST' });
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      alert('Could not open billing portal. Please try again.');
    }
  };

  if (isLoading || !isAuthenticated) return null;

  return (
    <nav style={{
      borderBottom: '1px solid #eee',
      background: '#fff', position: 'sticky', top: 0, zIndex: 900,
    }}>
      <div style={{
        maxWidth: 1600, margin: '0 auto',
        padding: '0.75rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxSizing: 'border-box',
      }}>
        <Link to={logoHref} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="Prism" style={{ height: 48, margin: '-6px 0' }} />
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {showPdfLens && (
            <button
              onClick={() => setPanelOpen(true)}
              style={{
                background: 'none', border: '1px solid #ddd', borderRadius: 6,
                padding: '0.35rem 0.8rem', cursor: 'pointer',
                fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#333',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}
            >
              Lens
              {items.length > 0 && (
                <span
                  key={pulseKey}
                  className="prism-badge-pulse"
                  style={{
                    background: '#ba9394', color: '#fff', borderRadius: 10,
                    fontSize: 11, fontWeight: 700, padding: '1px 6px',
                  }}
                >
                  {items.length}
                </span>
              )}
            </button>
          )}
          <UserMenu me={me} onBilling={handleBilling} onMyLenses={() => setMyLensesPanelOpen(true)} showLenses={showPdfLens} />
        </div>
      </div>
    </nav>
  );
}

function OrgCapacityBlockedPage({ organizationName, managerEmail }: { organizationName: string; managerEmail: string | null }) {
  return (
    <div style={{
      fontFamily: "'Montserrat', sans-serif", minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF8',
    }}>
      <div style={{ maxWidth: 480, width: '100%', padding: '3rem 2rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: 26, fontWeight: 500, margin: '0 0 0.75rem', color: '#000' }}>
          {organizationName} has reached its plan's user limit
        </h1>
        <p style={{ fontSize: 15, color: '#66635F', margin: 0 }}>
          {managerEmail
            ? <>Contact <strong>{managerEmail}</strong> to request an upgrade.</>
            : 'Contact your organization\'s Manager to request an upgrade.'}
        </p>
      </div>
    </div>
  );
}

function App() {
  const { orgCapacityError } = useUserContext();

  if (orgCapacityError) {
    return <OrgCapacityBlockedPage organizationName={orgCapacityError.organizationName} managerEmail={orgCapacityError.managerEmail} />;
  }

  return (
    <>
      <Nav />
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/account" element={<StorefrontPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/market/:id" element={<ProtectedRoute><MarketLandingPage /></ProtectedRoute>} />
        <Route path="/neighborhood/:id" element={<ProtectedRoute><NeighborhoodLandingPage /></ProtectedRoute>} />
        <Route path="/building/:id" element={<ProtectedRoute><BuildingLandingPage /></ProtectedRoute>} />
        <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
        <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      </Routes>
      </div>
      <AnalyticsPackagePanel />
      <MyLensesPanel />
      <LensSyncManager />
    </>
  );
}

export default App;
